import { FocusEvent, PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FloatingPortal } from '@floating-ui/react';

import { removeNotification, useNotificationStore } from 'stores/notifications/notifications';
import { cn } from 'support/helpers/cn/cn';
import { Notification } from 'components/Notification/Notification';

import { EASING, EDGE_FADE, LEAVE_MS, TRANSITION_MS, VIEWPORT_INSET } from './hooks/useNotificationStack/helpers';
import { useNotificationStack } from './hooks/useNotificationStack/useNotificationStack';

type NotificationListProps = {
  /** `null` keeps notifications up until they are dismissed by hand. Used by visual snapshots. */
  autoDismissMs?: number | null;
};

export const NotificationList = ({ autoDismissMs }: NotificationListProps = {}) => {
  const { notifications } = useNotificationStore();
  // Tracked apart so losing one cannot cancel the other. Dismissing a card destroys the focused
  // close button, and the resulting focusout would otherwise collapse the stack out from under a
  // pointer that never moved.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const expanded = hovered || focused;
  const [dismissing, setDismissing] = useState<Set<string>>(() => new Set());
  const [fade, setFade] = useState({ top: false, bottom: false });
  const [pinned, setPinned] = useState(true);

  const viewportRef = useRef<HTMLDivElement>(null);
  const dismissingRef = useRef(new Set<string>());

  const { layouts, canvasHeight, registerCard } = useNotificationStack({ notifications, expanded, dismissing });

  // Held locally for LEAVE_MS before the store drops it, so the card can animate out of a slot that
  // still exists.
  const dismiss = useCallback((id: string) => {
    if (dismissingRef.current.has(id)) {
      return;
    }

    dismissingRef.current.add(id);
    setDismissing(new Set(Array.from(dismissingRef.current)));
    window.setTimeout(() => removeNotification(id), LEAVE_MS);
  }, []);

  useEffect(() => {
    const liveIds = new Set(notifications.map((notification) => notification.id));
    const settled = Array.from(dismissingRef.current).filter((id) => !liveIds.has(id));

    if (settled.length === 0) {
      return;
    }

    settled.forEach((id) => dismissingRef.current.delete(id));
    setDismissing(new Set(Array.from(dismissingRef.current)));
  }, [notifications]);

  const syncScroll = useCallback(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const slack = viewport.scrollHeight - viewport.clientHeight;
    const atBottom = slack - viewport.scrollTop <= 4;

    setPinned(atBottom);
    setFade({ top: viewport.scrollTop > 4, bottom: slack > 4 && !atBottom });
  }, []);

  // The canvas snaps to its full height while the viewport animates, so keeping the newest card in
  // sight is a single assignment — the browser re-clamps scrollTop as the viewport grows.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;

    if (viewport && pinned) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  });

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setFocused(false);
  };

  const onPointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    // Re-laying the stack out can make Chromium fire pointerleave with the pointer stationary and
    // still over the stack. Acting on it would strand the deck collapsed under the cursor, because
    // no pointerenter can follow — the pointer never left. Trust the geometry over the event.
    const bounds = event.currentTarget.getBoundingClientRect();
    const stillInside =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;

    if (stillInside) {
      return;
    }

    setHovered(false);
    setPinned(true);
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <FloatingPortal>
      <div
        ref={viewportRef}
        aria-live="assertive"
        className="fixed right-0 bottom-0 z-50 max-h-dvh w-[min(100vw,31rem)] scrollbar-none overflow-y-auto overscroll-contain p-6 motion-reduce:transition-none [&::-webkit-scrollbar]:hidden"
        style={{
          height: canvasHeight + VIEWPORT_INSET * 2,
          transitionProperty: 'height',
          transitionDuration: `${TRANSITION_MS}ms`,
          transitionTimingFunction: EASING,
          maskImage: `linear-gradient(to bottom, transparent 0, #000 ${fade.top ? EDGE_FADE : 0}px, #000 calc(100% - ${fade.bottom ? EDGE_FADE : 0}px), transparent 100%)`,
          WebkitMaskImage: `linear-gradient(to bottom, transparent 0, #000 ${fade.top ? EDGE_FADE : 0}px, #000 calc(100% - ${fade.bottom ? EDGE_FADE : 0}px), transparent 100%)`,
        }}
        onPointerEnter={() => setHovered(true)}
        // Belt and braces for the same problem: any movement over the stack re-expands it, so a
        // stray leave can never strand it collapsed.
        onPointerMove={() => !hovered && setHovered(true)}
        onPointerLeave={onPointerLeave}
        onFocus={() => setFocused(true)}
        onBlur={onBlur}
        onScroll={syncScroll}
        onTransitionEnd={syncScroll}
      >
        <div className="relative min-h-full w-full">
          {layouts.map(({ notification, offsetY, scaleX, height, opacity, zIndex, interactive, leaving }) => {
            return (
              <div
                key={notification.id}
                inert={!interactive}
                className={cn(
                  'absolute inset-x-0 bottom-0 origin-bottom motion-reduce:transition-none',
                  // Only the slivers are cropped to a borrowed height; clipping a full card would
                  // cut off its shadow.
                  interactive ? '' : 'overflow-hidden',
                )}
                style={{
                  transform: `translate3d(${leaving ? 28 : 0}px, ${-offsetY}px, 0) scaleX(${scaleX})`,
                  height: height || undefined,
                  opacity,
                  zIndex,
                  transitionProperty: 'transform, height, opacity',
                  transitionDuration: `${leaving ? LEAVE_MS : TRANSITION_MS}ms`,
                  transitionTimingFunction: leaving ? 'ease-in' : EASING,
                }}
              >
                <div ref={registerCard(notification.id)} className="animate-notification-enter">
                  <Notification
                    headline={notification.title}
                    description={notification.subtitle}
                    type={notification.type}
                    autoDismissMs={autoDismissMs}
                    paused={expanded || leaving}
                    onDismiss={() => dismiss(notification.id)}
                  />
                </div>

                {/* A white card on a white page has no visible edge, and the card's shadow points
                    downwards so it never falls on the layers above it. Buried layers get an
                    explicit outline so the deck reads on any background. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-gray-300 ring-inset motion-reduce:transition-none"
                  style={{
                    opacity: interactive ? 0 : 1,
                    transitionProperty: 'opacity',
                    transitionDuration: `${TRANSITION_MS}ms`,
                    transitionTimingFunction: EASING,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </FloatingPortal>
  );
};
